import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {
    Controller,
    useForm,
    useWatch
} from "react-hook-form";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import ClearOutlinedIcon
    from "@material-ui/icons/ClearOutlined";
import {toast} from "react-toastify";
import {
    ToggleButton,
    ToggleButtonGroup
} from "@material-ui/lab";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import apiHelper from "../../../../util/ApiHelper";
import {modal} from "../../../../util";
import styled from "styled-components";

const useStylesAll = makeStyles((theme) => ({
    root: {
        margin: 0,
        marginBottom: 20,
        minWidth: "100%",
    },
    leftGrid: {
        maxHeight: 100,
    },
    last: {
        margin: 0,
        marginTop: 47,
    },
    topBar: {
        padding: 0,
    },
    pagination: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
    },
    "&::placeholder": {
        color: "grey",
    },
}));

const schema = yup.object().shape({
    code: yup.string().required("Code is required."),
    en: yup.string().required("English name required."),
    tc: yup.string().required("Chinese name required."),
    url: yup
        .string()
        .test("url", "Google map url is required.", (value, context) => {
            switch (context.parent.type.toLowerCase()) {
                case "venue":
                    if (!value) {
                        return false;
                    }
                    return true;
                case "remark":
                default:
                    return true;
            }
        })
        .test("url", "Missing http:// or https://", (value, context) => {
            switch (context.parent.type.toLowerCase()) {
                case "venue":
                    if (!RegExp(/http:\/\/|https:\/\//g).test(value)) {
                        return false;
                    }
                    return true;
                case "remark":
                default:
                    return true;
            }
        }),
});

const A = styled.a`
  color: #306F9D;
  text-decoration: none;
`;


export default function RemarksForm({disabled, ...props}) {
    const {history, fetchData} = props;
    const classes = useStylesAll();
    let {id} = useParams();

    const {
        control,
        register,
        handleSubmit,
        setValue
    } = useForm({
        defaultValues: {
            code: "",
            type: "Venue",
            en: "",
            tc: "",
            url: "",
            image: "",
        },
        resolver: yupResolver(schema),
    });

    // const [type, setType] = useState('venue');
    const [currImage, setCurrImage] = useState("");
    const [fileName, setFileName] = useState("");

    useEffect(() => {
        register("file");
    }, [register]);

    const handleChange = (e) => {
        setValue("file", e.target.files); // you get all the files object here
        if (e.target.files[0]) setFileName(e.target.files[0].name);
    };

    const type = useWatch({control, name: "type"});

    useEffect(() => {
        if (id) {
            apiHelper.cinefan
                .getRemarkById(id)
                .then((response) => {
                    console.log(response.data);
                    // setType(response.data.type);
                    setCurrImage(response.data.image);
                    setValue("type", response.data.type);
                    setValue("en", response.data.localizations.en.content);
                    setValue("tc", response.data.localizations.tc.content);
                    setValue("url", response.data.url);
                    setValue("code", response.data.code);
                })
                .catch(function (error) {
                    console.log(error);
                });
        }
    }, []);

    const handleDeleteImage = async () => {
        const decision = await modal.fire({
            title: "Delete remark image",
            text: "Are you sure to delete the current remark image?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            setCurrImage("");
        }
    };

    const handleDeleteRemark = async () => {
        const decision = await modal.fire({
            title: "Delete Remark",
            text: "Are you sure to delete the current remark?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.cinefan.deleteRemarkById(id);
                console.log(result.data);
                history.push("/cinefan/remarks");
            } catch (err) {
                console.log(err);
            }
        }
    };

    const updateRemark = async (data, image) => {
        try {
            const result = await apiHelper.cinefan.updateRemarkById(
                id,
                props.formatRowToData(data, null, image)
            );
            console.log(result.data);
            toast.dark("Your changes is saved.");
            history.push("/cinefan/remarks");
        } catch (err) {
            console.log(err);
        }
    };

    const createRemark = async (data, image) => {
        try {
            const result = await apiHelper.cinefan.postRemark(
                props.formatRowToData(data, props.minSeq(), image)
            );
            if (result.data.code === -4) {
                toast.error("Duplicated remark code.");
            } else {
                toast.dark("Remark created.");

                //clear all fields
                // setType(null);
                setCurrImage("");
                setValue("type", "Venue");
                setValue("en", "");
                setValue("tc", "");
                setValue("url", "");
                setValue("code", "");
                setFileName("");
                //refetch data => refresh table
                fetchData();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const onSubmit = (data) => {
        let image = "";
        const sendRemark = () => {
            if (id) {
                if (!image && currImage) {
                    image = currImage;
                }
                updateRemark(data, image);
            } else {
                createRemark(data, image);
            }
        };
        if (data.file) {
            apiHelper.cinefan
                .uploadFile(data.file[0])
                .then((response) => {
                    console.log(response.data);
                    image = response.data.url;
                })
                .then(() => {
                    sendRemark();
                })
                .catch((error) => console.log(error));
        } else {
            sendRemark();
        }
    };

    return (
        <>
            <Box fontWeight={900} fontSize={13} mb={2}
                 width="100%">
                <Typography
                    variant="p">{props.description}</Typography>
            </Box>
            <form autoComplete="off"
                  onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled}
                          className="fieldset">
                    <Controller
                        defaultValue={"Venue"}
                        name={`type`}
                        control={control}
                        render={({
                                     field: {
                                         value,
                                         onChange,
                                         ...rest
                                     },
                                     fieldState: {error},
                                 }) => (
                            <ToggleButtonGroup
                                {...rest}
                                value={value}
                                onChange={(event, newFormats) => {
                                    onChange(newFormats);
                                }}
                                exclusive
                                aria-label="period status"
                                size="large"
                                fullWidth
                            >
                                <ToggleButton
                                    className={classes.root}
                                    value="Venue"
                                    aria-label="venue"
                                    disabled={disabled}
                                >
                                    Venue
                                </ToggleButton>
                                <ToggleButton
                                    className={classes.root}
                                    value="Remark"
                                    aria-label="remark"
                                    disabled={disabled}
                                >
                                    Remark
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    />
                    {/* <Box width={props.upperBtnGroupSize}>
          <ButtonGroup size="large" fullWidth={true}>
            <Button
              className={classes.root}
              variant={isVenue ? "contained" : "outlined"}
              color="primary"
              onClick={() => setIsVenue(true)}
            >
              Venue
            </Button>
            <Button
              className={classes.root}
              variant={!isVenue ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                setIsVenue(false);
                setValue("googleMapUrl", "");
              }}
            >
              Remark
            </Button>
          </ButtonGroup>
        </Box> */}
                    <Box width="100%">
                        <Controller
                            control={control}
                            name="code"
                            defaultValue=""
                            render={({
                                         field,
                                         fieldState: {error}
                                     }) => {
                                return (
                                    <TextField
                                        id="outlined-basic"
                                        className={classes.root}
                                        label="Code"
                                        variant="outlined"
                                        error={!!error}
                                        helperText={
                                            error
                                                ? error.message
                                                : "The code should be unique for each remark."
                                        }
                                        {...field}
                                        fullWidth
                                    />
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            name="en"
                            defaultValue=""
                            render={({
                                         field,
                                         fieldState: {error}
                                     }) => {
                                return (
                                    <TextField
                                        id="outlined-basic"
                                        className={classes.root}
                                        label="Name (English)"
                                        variant="outlined"
                                        error={!!error}
                                        helperText={
                                            error
                                                ? error.message
                                                : "The name is how it appears on your site."
                                        }
                                        {...field}
                                        fullWidth
                                    />
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            name="tc"
                            defaultValue=""
                            render={({
                                         field,
                                         fieldState: {error}
                                     }) => {
                                return (
                                    <TextField
                                        id="outlined-basic"
                                        className={classes.root}
                                        label="Name (Traditional Chinese)"
                                        variant="outlined"
                                        error={!!error}
                                        helperText={
                                            error
                                                ? error.message
                                                : "The name is how it appears on your site."
                                        }
                                        {...field}
                                        fullWidth
                                    />
                                );
                            }}
                        />

                        <TextField
                            htmlFor="raised-button-file"
                            label={
                                fileName
                                    ? currImage
                                        ? "Change Remark Image"
                                        : "Remark Image"
                                    : ""
                            }
                            className={classes.root}
                            value={fileName ? fileName : ""}
                            variant="outlined"
                            helperText={
                                currImage ? "" : "The image will display as remark icon."
                            }
                            placeholder={currImage ? "Change Remark Image" : "Remark Image"}
                            fullWidth
                            InputProps={{
                                classes: {
                                    input: classes["&::placeholder"],
                                },
                                endAdornment: (
                                    <Controller
                                        control={control}
                                        name="file"
                                        render={({field}) => {
                                            return (
                                                <InputAdornment
                                                    position="end">
                                                    <input
                                                        style={{display: "none"}}
                                                        id="raised-button-file"
                                                        type="file"
                                                        onChange={handleChange}
                                                    />
                                                    <Button
                                                        htmlFor="raised-button-file"
                                                        variant="outlined"
                                                        component="label"
                                                    >
                                                        Choose
                                                    </Button>
                                                </InputAdornment>
                                            );
                                        }}
                                    />
                                ),
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        style={fileName ? {} : {display: "none"}}
                                    >
                                        <IconButton
                                            component="label"
                                            size="small"
                                            onClick={() => {
                                                setValue("file", null);
                                                setFileName("");
                                            }}
                                        >
                                            <ClearOutlinedIcon
                                                fontSize="inherit"/>
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            style={currImage ? {marginBottom: "0"} : {}}
                        />

                        {currImage ? (
                            <p>
                                <img
                                    src={currImage}
                                    style={{
                                        maxHeight: "30px",
                                        maxWidth: "30px",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => window.open(currImage, '_blank').focus()}
                                    alt=""
                                />
                                &nbsp;
                                <A href={currImage}
                                   target="_blank">
                                    <small>{currImage.split("/").pop()}</small>
                                </A>
                                {disabled || (
                                    <small
                                        style={{
                                            marginLeft: "20px",
                                            textDecoration: "underline",
                                            color: "#962013",
                                            cursor: "pointer",
                                        }}
                                        onClick={handleDeleteImage}
                                    >
                                        Delete
                                    </small>
                                )}
                            </p>
                        ) : (
                            ""
                        )}
                        {type && "venue" === type.toLowerCase() ? (
                            <Controller
                                control={control}
                                name="url"
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => {
                                    return (
                                        <TextField
                                            id="outlined-basic"
                                            className={classes.root}
                                            label="Google map URL"
                                            variant="outlined"
                                            fullWidth
                                            {...field}
                                            error={!!error}
                                            helperText={
                                                error
                                                    ? error.message
                                                    : "Users will be directed to the location in google map if url is provided."
                                            }
                                        />
                                    );
                                }}
                            />
                        ) : (
                            <></>
                        )}
                        <Box display="flex"
                             alignItems="flex-end"
                             alignContent="flex-end">
                            {disabled || (
                                <Box>
                                    <Button
                                        className={classes.last}
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        type="submit"
                                    >
                                        {props.submitBtnTxt}
                                    </Button>
                                </Box>
                            )}
                            {id && !disabled ? (
                                <Box>
                                    <small
                                        style={{
                                            marginLeft: "20px",
                                            color: "#962013",
                                            cursor: "pointer",
                                        }}
                                        onClick={handleDeleteRemark}
                                    >
                                        Delete Remark
                                    </small>
                                </Box>
                            ) : (
                                <></>
                            )}
                        </Box>
                    </Box>
                </fieldset>
            </form>
        </>
    );
}
